import { ArrowLeft, Shield, Database, Share2, Lock, UserCheck, Mail } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";

const PrivacyPolicy = () => {
  const navigate = useNavigate();
  const lastUpdated = "January 30, 2025";

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-background/95 backdrop-blur border-b">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-xl font-bold">Privacy Policy</h1>
            <p className="text-sm text-muted-foreground">Last updated: {lastUpdated}</p>
          </div>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-8 space-y-8">
        {/* Introduction */}
        <section className="space-y-4">
          <p className="text-lg text-muted-foreground">
            SORIN ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our mobile application and related services (collectively, the "Service").
          </p>
          <p className="text-muted-foreground">
            Please read this privacy policy carefully. By using the Service, you agree to the collection and use of information in accordance with this policy.
          </p>
        </section>

        <Separator />

        {/* Information We Collect */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <Database className="h-6 w-6 text-primary" />
            <CardTitle>Information We Collect</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Personal Information</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Name and display name</li>
                <li>Email address</li>
                <li>Phone number (optional)</li>
                <li>Job position and department</li>
                <li>Profile photo (optional)</li>
              </ul>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Usage Information</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Safety reports and hazard observations you submit</li>
                <li>Pre-shift check-in responses</li>
                <li>Learning module progress and quiz results</li>
                <li>App usage patterns and feature interactions</li>
                <li>Device information (device type, operating system, unique device identifiers)</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Media and Location</h4>
              <ul className="list-disc list-inside text-muted-foreground space-y-1">
                <li>Photos you upload for hazard reports (with optional face blurring)</li>
                <li>Voice recordings for transcription (processed and deleted after transcription)</li>
                <li>Location data (only when you submit a report, with your permission)</li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* How We Use Your Information */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <Shield className="h-6 w-6 text-primary" />
            <CardTitle>How We Use Your Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">We use the collected information for:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong>Safety Management:</strong> Processing and routing hazard reports to appropriate supervisors for resolution</li>
              <li><strong>Risk Assessment:</strong> Analyzing patterns to identify workplace safety risks and recommend interventions</li>
              <li><strong>Learning & Development:</strong> Providing personalized safety training and tracking progress</li>
              <li><strong>Gamification:</strong> Managing XP, badges, streaks, and leaderboard rankings to encourage safety participation</li>
              <li><strong>Communication:</strong> Sending notifications about report status, clarification requests, and safety alerts</li>
              <li><strong>Analytics:</strong> Generating anonymized insights for your organization's safety performance</li>
              <li><strong>Service Improvement:</strong> Understanding usage patterns to enhance app features and user experience</li>
            </ul>
          </CardContent>
        </Card>

        {/* Data Sharing */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <Share2 className="h-6 w-6 text-primary" />
            <CardTitle>How We Share Your Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Within Your Organization</h4>
              <p className="text-muted-foreground">
                Your safety reports and relevant profile information are shared with supervisors and safety managers within your organization to facilitate hazard resolution and safety management.
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-2">Service Providers</h4>
              <p className="text-muted-foreground">
                We may share information with third-party service providers who assist us in operating our Service, including:
              </p>
              <ul className="list-disc list-inside text-muted-foreground mt-2 space-y-1">
                <li>Cloud hosting and data storage providers</li>
                <li>AI/ML services for natural language processing and image analysis</li>
                <li>Push notification services</li>
                <li>Analytics providers</li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-2">Legal Requirements</h4>
              <p className="text-muted-foreground">
                We may disclose your information if required by law, court order, or government request, or to protect our rights, privacy, safety, or property.
              </p>
            </div>

            <div className="bg-muted/50 p-4 rounded-lg">
              <p className="text-sm font-medium">
                We do NOT sell your personal information to third parties for marketing purposes.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Data Security */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <Lock className="h-6 w-6 text-primary" />
            <CardTitle>Data Security</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              We implement appropriate technical and organizational security measures to protect your personal information, including:
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Encryption of data in transit (TLS/SSL) and at rest</li>
              <li>Secure authentication with session management</li>
              <li>Role-based access controls within organizations</li>
              <li>Regular security audits and vulnerability assessments</li>
              <li>Automatic face blurring in uploaded photos to protect worker privacy</li>
              <li>Row-level security policies ensuring users only access authorized data</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              While we strive to protect your information, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
            </p>
          </CardContent>
        </Card>

        {/* Your Rights */}
        <Card>
          <CardHeader className="flex flex-row items-center gap-3">
            <UserCheck className="h-6 w-6 text-primary" />
            <CardTitle>Your Rights and Choices</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">Depending on your location, you may have the following rights:</p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li><strong>Access:</strong> Request a copy of your personal data</li>
              <li><strong>Correction:</strong> Request correction of inaccurate data</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal retention requirements)</li>
              <li><strong>Portability:</strong> Request transfer of your data to another service</li>
              <li><strong>Opt-out:</strong> Opt out of certain data processing activities</li>
              <li><strong>Notifications:</strong> Manage push notification preferences in app settings</li>
            </ul>
            <p className="text-muted-foreground mt-4">
              To exercise these rights, please contact your organization's administrator or reach out to us directly.
            </p>
          </CardContent>
        </Card>

        {/* Data Retention */}
        <Card>
          <CardHeader>
            <CardTitle>Data Retention</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              We retain your personal information for as long as necessary to provide the Service and fulfill the purposes outlined in this policy, unless a longer retention period is required by law or your organization's policies.
            </p>
            <ul className="list-disc list-inside text-muted-foreground space-y-2">
              <li>Safety reports and incident data may be retained for regulatory compliance purposes</li>
              <li>Account data is retained until account deletion is requested</li>
              <li>Usage analytics are anonymized after 12 months</li>
              <li>Voice recordings are deleted immediately after transcription</li>
            </ul>
          </CardContent>
        </Card>

        {/* Children's Privacy */}
        <Card>
          <CardHeader>
            <CardTitle>Children's Privacy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Our Service is not intended for individuals under the age of 18. We do not knowingly collect personal information from children. If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </CardContent>
        </Card>

        {/* International Data Transfers */}
        <Card>
          <CardHeader>
            <CardTitle>International Data Transfers</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              Your information may be transferred to and processed in countries other than your country of residence. These countries may have data protection laws different from those in your country. We take appropriate safeguards to ensure your information remains protected in accordance with this Privacy Policy.
            </p>
          </CardContent>
        </Card>

        {/* Changes to This Policy */}
        <Card>
          <CardHeader>
            <CardTitle>Changes to This Privacy Policy</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-muted-foreground">
              We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date. You are advised to review this Privacy Policy periodically for any changes.
            </p>
          </CardContent>
        </Card>

        {/* Contact Us */}
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="flex flex-row items-center gap-3">
            <Mail className="h-6 w-6 text-primary" />
            <CardTitle>Contact Us</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-muted-foreground">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="space-y-2">
              <p><strong>Email:</strong> privacy@sorinsafety.com</p>
              <p><strong>Website:</strong> https://sorinsafety.lovable.app</p>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground py-8">
          <p>© {new Date().getFullYear()} SORIN Safety. All rights reserved.</p>
        </div>
      </main>
    </div>
  );
};

export default PrivacyPolicy;
